import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import * as Yup from 'yup'
import { Formik, Form, FormikHelpers } from 'formik'
import { OrderFormSchema, TOrderForm } from './OrderForm.interface'
import { Button, Popconfirm } from 'antd'
import {
  AntdAutoCompleteValidateField,
  AntdInputValidateField,
} from '../FormikForm/AntdValidateField'
import {
  createOrder,
  updateOrder,
  softDeleteOrder,
} from '@/supabase/Orderbook/orderbook'
import { useCustomers } from '@/hooks/useCustomers'
import {
  TOrderFormStatus,
  setOrderFormStatus,
  setOrderIdForEdit,
} from '@/state/orderManager/reducer'
import { useAppDispatch } from '@/state/hooks'
import { batch } from 'react-redux'
import { useToastApplication } from '@/state/application/hooks'
import { updateEmptyStringsToNull } from '@/utils'
import { usePricingData } from '@/hooks/usePricingData'
import { MonitorIcon } from 'lucide-react'

import { PricingDisplayInline } from './PricingDisplayInline'
import { OrderTotalDisplay } from './OrderTotalDisplay'



const validateForm = async (formData: TOrderForm) => {
  try {
    await OrderFormSchema.validate(formData, { abortEarly: false })
    return { isValid: true, errors: [] }
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      return {
        isValid: false,
        errors: err.inner.map(error => ({
          path: error.path,
          message: error.message,
        })),
      }
    }
    return {
      isValid: false,
      errors: [{ path: null, message: 'Validation failed' }],
    }
  }
}

export interface IOrderFormProps {
  orderFormStatus: TOrderFormStatus
  formData: TOrderForm
  orderIdForEdit?: number | null
}

// Component สำหรับแสดง input field พร้อมราคา
interface PricedInputFieldProps {
  label: string
  name: string
  placeholder: string
  value: number | null | undefined
  unitPrice: number
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
}

const PricedInputField: FC<PricedInputFieldProps> = ({
  label,
  name,
  placeholder,
  value,
  unitPrice,
  onChange,
  onBlur,
}) => {
  const quantity = value || 0

  return (
    <div className='flex items-center gap-4 py-2'>
      <div className='w-14 text-right text-sm font-medium text-gray-700 leading-relaxed'>{label}</div>
      <div className='flex-1'>
        <AntdInputValidateField
          name={name}
          type='number'
          className='text-filed-arrow-hide arrow-hide h-10'
          placeholder={placeholder}
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
        />
      </div>
      <PricingDisplayInline
        quantity={quantity}
        unitPrice={unitPrice}
      />
    </div>
  )
}

export const OrderForm: FC<IOrderFormProps> = ({
  orderFormStatus,
  formData,
  orderIdForEdit,
}) => {
  const { fail } = useToastApplication()
  const customers = useCustomers()
  const customerOptions = useMemo(
    () => {
      if (!customers || customers.length === 0) {
        return []
      }
      
      // กรองเฉพาะลูกค้าประจำ (REGULAR) และไม่ใช่ default customer
      const options = customers
        .filter(customer => 
          customer.id !== 'default' && 
          customer.type === 'REGULAR'
        )
        .map(customer => ({
          value: customer.name || customer.id,
          label: customer.name || customer.id,
          customerId: customer.id, // เก็บ ID ไว้สำหรับการอ้างอิง
        }))
      return options
    },
    [customers]
  )
  const dispatch = useAppDispatch()

  // State to track the currently selected customer ID and name
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(formData.customerId || '')
  const [customerName, setCustomerName] = useState<string>('')

  // ดึงข้อมูลราคาตาม customer ที่เลือก
  const { pricingData, isLoading } = usePricingData(selectedCustomerId)

  // Sync customerName with formData changes (for edit mode)
  useEffect(() => {
    if (formData.customerId) {
      // หาชื่อลูกค้าจาก customerId
      const customer = customers?.find(c => c.id === formData.customerId)
      if (customer) {
        // ลูกค้าประจำที่มีอยู่ในระบบ
        setCustomerName(customer.name || customer.id)
        setSelectedCustomerId(customer.id)
      } else {
        // ลูกค้าใหม่หรือลูกค้าทั่วไป - แสดง customerId เป็นชื่อ
        setCustomerName(formData.customerId)
        setSelectedCustomerId('') // ใช้ default pricing
      }
    } else {
      // ถ้าไม่มี customerId ให้ clear
      setCustomerName('')
      setSelectedCustomerId('')
    }
  }, [formData.customerId, customers])
  


  const handleBtnClickCancel = useCallback(() => {
    batch(() => {
      dispatch(setOrderFormStatus('CREATE'))
      dispatch(setOrderIdForEdit(null))
    })
  }, [dispatch])

  const handleBtnClickDelete = useCallback(async () => {
    if (!orderIdForEdit) {
      throw new Error(`Not found orderId for delete order`)
    }
    await softDeleteOrder(orderIdForEdit)

    batch(() => {
      dispatch(setOrderFormStatus('CREATE'))
      dispatch(setOrderIdForEdit(null))
    })
  }, [dispatch, orderIdForEdit])

  const handleSubmitForm = useCallback(
    async (formsData: TOrderForm, { resetForm }: FormikHelpers<TOrderForm>) => {
      try {
        const validateTestCase = await validateForm(
          updateEmptyStringsToNull(formsData)
        )
        if (!validateTestCase.isValid) {
          throw new Error(validateTestCase.errors[0]?.message)
        }

        if (orderFormStatus === 'CREATE') {
          await createOrder(formsData)
        } else if (orderFormStatus === 'UPDATE') {
          if (!orderIdForEdit) {
            throw new Error('Not found order id for updating order.')
          }

          await updateOrder(orderIdForEdit, formsData)
          batch(() => {
            dispatch(setOrderFormStatus('CREATE'))
            dispatch(setOrderIdForEdit(null))
          })
        }

        resetForm()
        // Clear local customer name state after successful submit
        setCustomerName('')
        setSelectedCustomerId('')
      } catch (error: unknown) {
        fail({ message: (error as Error)?.message || 'An error occurred' })
      }
    },
    [orderFormStatus, orderIdForEdit, dispatch, fail]
  )

  return (
    <Formik
      initialValues={formData}
      validationSchema={OrderFormSchema}
      enableReinitialize
      onSubmit={handleSubmitForm}
    >
      {({ values, handleChange, handleBlur, setFieldValue }) => {
        const onCustomerChange = (inputValue: string) => {
          // Handle undefined/null from allowClear
          const value = inputValue ?? ''
          setCustomerName(value)
          
          // หา customer ID จาก name ถ้ามีใน customerOptions
          const customer = customers?.find(c => c.name === value)
          if (customer) {
            // ลูกค้าเก่าที่มีอยู่แล้ว
            setFieldValue('customerId', customer.id)
            setSelectedCustomerId(customer.id)
          } else if (value.trim()) {
            // ลูกค้าใหม่ - สร้าง ID ชั่วคราวจากชื่อ และใช้ default pricing
            const tempCustomerId = `new_customer_${value.trim().replace(/\s+/g, '_').toLowerCase()}`
            setFieldValue('customerId', tempCustomerId)
            setSelectedCustomerId('') // ใช้ default pricing
          } else {
            // ถ้าไม่มีชื่อ ให้ clear form
            setFieldValue('customerId', '')
            setSelectedCustomerId('')
          }
        }

        const onCustomerSelect = (selectedValue: string) => {
          // เมื่อเลือกจาก dropdown (ลูกค้าเก่า)
          const customer = customers?.find(c => c.name === selectedValue)
          if (customer) {
            setCustomerName(selectedValue)
            setFieldValue('customerId', customer.id)
            setSelectedCustomerId(customer.id)
          }
        }

        // แสดงชื่อลูกค้าใน input
        const displayValue = customerName || 
          (customers?.find(c => c.id === values.customerId)?.name) || 
          values.customerId || ''

        // ตรวจสอบว่ามีสินค้าหรือไม่
        const hasItems = pricingData && (
          (values.largeIceCubeAmount || 0) > 0 ||
          (values.smallIceCubeAmount || 0) > 0 ||
          (values.crushedIceAmount || 0) > 0 ||
          (values.coarselyCrushedIceAmount || 0) > 0
        )

        return (
          <Form noValidate className='flex flex-col gap-4 w-full'>
            {/* Customer Selection */}
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-gray-700'>
                Customer
              </label>
              <AntdAutoCompleteValidateField
                name='customerId'
                placeholder='Select or enter customer name'
                showSearch
                allowClear
                className='w-full h-9 rounded'
                value={displayValue}
                options={customerOptions}
                onChange={onCustomerChange}
                onSelect={onCustomerSelect}
                onClear={() => {
                  setCustomerName('')
                  setFieldValue('customerId', '')
                  setSelectedCustomerId('')
                }}
                filterOption={(input, option) => {
                  const label = (option as { label?: string })?.label?.toLowerCase() ?? ''
                  return label.includes(input?.toLowerCase())
                }}
              />
            </div>

            {/* Product Selection */}
            <div className='space-y-2'>
              <label className='block text-xs font-medium text-gray-700 mb-1'>
                Products
              </label>

              {isLoading ? (
                <div className='text-center py-4 text-gray-500'>
                  <div className='animate-pulse text-sm'>Loading pricing...</div>
                </div>
              ) : pricingData ? (
                <div className='bg-white border rounded p-3 space-y-2'>
                  <PricedInputField
                    label='ใหญ่'
                    name='largeIceCubeAmount'
                    placeholder='0'
                    value={values.largeIceCubeAmount}
                    unitPrice={pricingData.largeIceCubePriceTHB}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <PricedInputField
                    label='เล็ก'
                    name='smallIceCubeAmount'
                    placeholder='0'
                    value={values.smallIceCubeAmount}
                    unitPrice={pricingData.smallIceCubePriceTHB}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <PricedInputField
                    label='โม่'
                    name='crushedIceAmount'
                    placeholder='0'
                    value={values.crushedIceAmount}
                    unitPrice={pricingData.crushedIcePriceTHB}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <PricedInputField
                    label='โม่หยาบ'
                    name='coarselyCrushedIceAmount'
                    placeholder='0'
                    value={values.coarselyCrushedIceAmount}
                    unitPrice={pricingData.crushedIcePriceTHB}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              ) : (
                <div className='text-center py-4 text-red-500 bg-red-50 rounded text-sm'>
                  Unable to load pricing data
                </div>
              )}

              {/* แสดงยอดรวมถ้ามีสินค้า */}
              {hasItems && pricingData && (
                <OrderTotalDisplay
                  largeIceCubeAmount={values.largeIceCubeAmount || 0}
                  smallIceCubeAmount={values.smallIceCubeAmount || 0}
                  crushedIceAmount={values.crushedIceAmount || 0}
                  coarselyCrushedIceAmount={values.coarselyCrushedIceAmount || 0}
                  pricingData={pricingData}
                  customerId={values.customerId}
                />
              )}
            </div>

            {/* Monitor Selection */}
            <div className='space-y-1'>
              <label className='block text-xs font-medium text-gray-700'>
                Display
              </label>
              <div className='flex gap-2 w-full'>
                {/* Hide Queue button when editing an order already assigned to a monitor */}
                {!(orderFormStatus === 'UPDATE' && formData.monitorNumber > 0) && (
                  <button
                    type='button'
                    onClick={() => setFieldValue('monitorNumber', 0)}
                    className={`flex-1 h-8 px-3 text-sm rounded border transition-colors ${
                      values.monitorNumber === 0
                        ? 'bg-gray-600 text-white border-gray-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Queue
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => setFieldValue('monitorNumber', 1)}
                  className={`flex-1 h-8 px-3 text-sm rounded border transition-colors flex items-center justify-center gap-1 ${
                    values.monitorNumber === 1
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-300'
                  }`}
                >
                  <MonitorIcon size={14} />
                  1
                </button>
                <button
                  type='button'
                  onClick={() => setFieldValue('monitorNumber', 2)}
                  className={`flex-1 h-8 px-3 text-sm rounded border transition-colors flex items-center justify-center gap-1 ${
                    values.monitorNumber === 2
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-300'
                  }`}
                >
                  <MonitorIcon size={14} />
                  2
                </button>
              </div>
            </div>

            {orderFormStatus === 'CREATE' && (
              <Button
                type='primary'
                htmlType='submit'
                size='large'
                className='w-full h-10 text-sm font-medium rounded mt-4'
              >
                Create Order
              </Button>
            )}
            {orderFormStatus === 'UPDATE' && (
              <div className='flex gap-2 mt-4'>
                <Popconfirm
                  title='Delete Order'
                  description='Are you sure you want to delete this order?'
                  okText='Delete'
                  cancelText='Cancel'
                  okButtonProps={{
                    style: { fontSize: '12px' },
                  }}
                  cancelButtonProps={{
                    style: { fontSize: '12px' },
                  }}
                  onConfirm={handleBtnClickDelete}
                >
                  <Button 
                    type='primary' 
                    danger
                    size='middle'
                    className='flex-1 h-9 rounded text-xs'
                  >
                    Delete
                  </Button>
                </Popconfirm>
                <Button
                  type='default'
                  size='middle'
                  className='flex-1 h-9 rounded text-xs'
                  onClick={handleBtnClickCancel}
                >
                  Cancel
                </Button>
                <Button
                  type='primary'
                  htmlType='submit'
                  size='middle'
                  className='flex-[2] h-9 rounded text-xs font-medium'
                >
                  Save Changes
                </Button>
              </div>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
